import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { fileUrl } from "./ctx";
import { toast } from "sonner";
import { Phone, Send, ImagePlus, RotateCcw, X, ShieldAlert, FileText, Wrench, UserPlus, Loader2, BadgeCheck, UserRound } from "lucide-react";
import { V } from "@/lib/vocab";
import { scrollToEnd } from "@/lib/scroll";

// Reusable live SMS demo — a real Twilio-free conversation against a community's own brain
// (its documents + roster). Used both on the per-community Live Demo page and inside the
// super-admin Voice & SMS Simulator (Demo Studio), where the community is picker-selected.
export default function LiveDemoPanel({ tenantId, tenant: tenantProp }) {
  const [tenant, setTenant] = useState(tenantProp || null);
  const [households, setHouseholds] = useState([]);
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!tenantId) return;
    setConv(null); setMessages([]); setText(""); setMedia([]);
    api.get(`/tenants/${tenantId}/households`).then((r) => {
      setHouseholds(r.data);
      const known = r.data.find((h) => h.phone);
      setPhone(known?.phone || "+15125550012");
    }).catch(() => {});
    if (tenantProp && tenantProp.id === tenantId) { setTenant(tenantProp); return; }
    api.get(`/tenants/${tenantId}`).then((r) => setTenant(r.data)).catch(() => setTenant(tenantProp || null));
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { scrollToEnd(scrollRef.current); }, [messages]);

  const loadConversation = async (id) => {
    const r = await api.get(`/conversations/${id}`);
    setConv(r.data.conversation);
    setMessages(r.data.messages);
  };

  const quickPhones = [
    ...households.filter((h) => h.phone).slice(0, 4).map((h) => ({
      phone: h.phone,
      name: h.people?.[0]?.name?.split(" ")[0] || `${V.one("person")}`,
      label: `${h.people?.[0]?.name?.split(" ")[0] || `${V.one("person")}`}${homeLabel(h) ? ` · ${homeLabel(h)}` : ""}`,
      verified: h.verified !== false,
      kind: "resident",
    })),
    { phone: "+15129998888", name: "Unknown", label: "Unknown number", verified: false, kind: "unknown" },
  ];

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia((m) => [...m, r.data.path]);
    } catch { toast.error("Upload failed"); }
  };

  const sendMessage = async (phoneArg, textArg, mediaArg = []) => {
    if (!textArg.trim() && mediaArg.length === 0) return;
    setSending(true);
    try {
      const r = await api.post("/sim/inbound", { tenant_id: tenantId, phone: phoneArg, text: textArg, media: mediaArg });
      await loadConversation(r.data.conversation_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const send = async () => {
    if (!text.trim() && media.length === 0) return;
    const t = text, m = media;
    setText(""); setMedia([]);
    await sendMessage(phone, t, m);
  };

  const reset = async (phoneArg = phone) => {
    try { await api.post("/sim/reset", { tenant_id: tenantId, phone: phoneArg }); } catch {}
    setConv(null); setMessages([]); setText(""); setMedia([]);
  };

  const knownPhone = households.find((h) => h.phone)?.phone || "+15125550012";
  const unknownPhone = "+15129998888";
  const spouseName = households.find((h) => h.phone)?.people?.[0]?.name?.split(" ")[0] || "Mike";
  const SCENARIOS = V.isHoa ? [
    { id: "cited", icon: FileText, label: "Cited answer", desc: `Pulls a real answer from this ${V.lc("org")}'s documents, with citations`, useKnown: true, text: "How many dogs am I allowed to have?" },
    { id: "hours", icon: FileText, label: "Amenity hours", desc: `Everyday question ${V.lcs("person")} ask constantly`, useKnown: true, text: "What are the pool hours?" },
    { id: "maintenance", icon: Wrench, label: "Maintenance request", desc: "Turns a complaint into a logged request automatically", useKnown: true, text: "The pool gate is broken and won't latch — can someone come fix it?" },
    { id: "identity", icon: UserPlus, label: "Identity re-map", desc: "Recognizes a family member texting from a new number", useKnown: true, text: `Hi, this is ${spouseName}'s spouse` },
    { id: "guardrail", icon: ShieldAlert, label: "Guardrail + escalate", desc: "Refuses legal advice and routes it to a human", useKnown: true, text: `I want to sue the ${V.grp()} — what should I do?` },
    { id: "register", icon: UserPlus, label: `New ${V.lc("person")} sign-up`, desc: "Unknown number self-registers and verifies", useKnown: false, text: "Hi, I just moved into unit 14 and want to get set up" },
  ] : [
    { id: "cited", icon: FileText, label: "Cited answer", desc: `Pulls a real answer from this ${V.lc("org")}'s documents, with citations`, useKnown: true, text: "Do you offer free estimates, and how long does a quote take?" },
    { id: "hours", icon: FileText, label: "Warranty question", desc: `Everyday question ${V.lcs("person")} ask constantly`, useKnown: true, text: "Is the work you did on my roof still under warranty?" },
    { id: "maintenance", icon: Wrench, label: `Urgent ${V.lc("request")}`, desc: `Turns a problem into a logged ${V.lc("request")} automatically`, useKnown: true, text: "Water is coming through my ceiling right now — can someone come out today?" },
    { id: "identity", icon: UserPlus, label: "Identity re-map", desc: "Recognizes a family member texting from a new number", useKnown: true, text: `Hi, this is ${spouseName}'s spouse` },
    { id: "guardrail", icon: ShieldAlert, label: "Guardrail + escalate", desc: "Won't invent a price or discount — routes it to the office", useKnown: true, text: "Can you knock 20% off if I pay cash today?" },
    { id: "register", icon: UserPlus, label: `New ${V.lc("person")} sign-up`, desc: "Unknown number asks for a quote and gets set up", useKnown: false, text: "Hi, I need a quote for a roof at 45 Maple Court" },
  ];

  const runScenario = async (s) => {
    if (sending || runningId) return;
    const p = s.useKnown ? knownPhone : unknownPhone;
    setRunningId(s.id);
    setPhone(p);
    try {
      await reset(p);
      await sendMessage(p, s.text, []);
    } finally { setRunningId(null); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8 items-start">
      {/* Phone */}
      <div>
        <div className="w-full max-w-[360px] mx-auto rounded-[2.75rem] bg-ink p-3 shadow-2xl shadow-ink/30">
          <div className="rounded-[2.25rem] bg-sand-50 overflow-hidden flex flex-col h-[620px]">
            <div className="bg-redline-500 text-white px-5 py-3.5 flex items-center gap-2">
              <Phone className="h-4 w-4" strokeWidth={1.5} />
              <div>
                <div className="text-sm font-semibold leading-none">{tenant?.persona_name || tenant?.name || "Concierge"}</div>
                <div className="text-[11px] text-white/70 mt-0.5">{tenant?.phone_number || "SMS demo"}</div>
              </div>
              {conv?.status === "escalated" && (
                <span className="ml-auto text-[10px] bg-white/25 px-2 py-1 rounded-full flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3" /> Escalated
                </span>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-sm text-muted-foreground mt-12 px-6">
                  Tap a scenario, or text {tenant?.persona_name || "the concierge"} as {V.a("person")} from <span className="font-semibold">{phone}</span>.
                </div>
              )}
              {messages.map((m) => {
                const resident = m.direction === "inbound";
                return (
                  <div key={m.id} className={`flex ${resident ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.direction}`}>
                    <div className="max-w-[82%]">
                      <div className={`px-3.5 py-2.5 text-sm leading-snug ${resident ? "bg-redline-500 text-white rounded-2xl rounded-tr-md" : "bg-white text-ink border border-sand-200 rounded-2xl rounded-tl-md"}`}>
                        {(m.media || []).map((p) => (
                          <img key={p} src={fileUrl(p)} alt="attachment" className="rounded-lg mb-2 max-h-40 object-cover" />
                        ))}
                        {m.body}
                      </div>
                      {(m.sources || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {m.sources.map((s, i) => (
                            <span key={i} className="text-[10px] text-redline-700 bg-redline-50 rounded-md px-2 py-0.5 flex items-center gap-1">
                              <FileText className="h-3 w-3" /> {s.title} · {s.section}
                            </span>
                          ))}
                        </div>
                      )}
                      {m.is_human && <div className="text-[10px] text-muted-foreground mt-1">— sent by {m.sent_by}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-sand-200 p-3 bg-white">
              {media.length > 0 && (
                <div className="flex gap-2 mb-2">
                  {media.map((p) => (
                    <div key={p} className="relative">
                      <img src={fileUrl(p)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                      <button onClick={() => setMedia((m) => m.filter((x) => x !== p))} className="absolute -top-1.5 -right-1.5 bg-ink text-white rounded-full h-5 w-5 flex items-center justify-center">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button data-testid="sim-attach-btn" onClick={() => fileRef.current?.click()} className="text-muted-foreground hover:text-redline-500 transition-colors">
                  <ImagePlus className="h-5 w-5" strokeWidth={1.5} />
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} />
                <input data-testid="sim-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Text message"
                  className="flex-1 bg-sand-50 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                <button data-testid="sim-send-btn" onClick={send} disabled={sending}
                  className="h-10 w-10 rounded-full bg-redline-500 text-white flex items-center justify-center hover:bg-redline-600 transition-colors disabled:opacity-50">
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <button data-testid="sim-reset-btn" onClick={() => reset()} className="mx-auto mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-ink transition-colors">
          <RotateCcw className="h-3.5 w-3.5" /> New conversation
        </button>
      </div>

      {/* Controls */}
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="text-sm font-semibold mb-1">Try a scenario</div>
          <p className="text-xs text-muted-foreground mb-4">One tap sends a realistic {V.lc("person")} message and shows exactly how the AI responds — no need to think one up.</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {SCENARIOS.map((s) => (
              <button key={s.id} data-testid={`sim-scenario-${s.id}`} onClick={() => runScenario(s)} disabled={sending || !!runningId}
                className="group text-left rounded-xl border border-sand-200 bg-sand-50/50 p-3.5 hover:border-redline-400 hover:bg-redline-50/40 transition-colors disabled:opacity-50">
                <div className="flex items-center gap-2 font-semibold text-sm text-ink">
                  <span className="h-7 w-7 rounded-lg bg-redline-50 flex items-center justify-center shrink-0 group-hover:bg-redline-100 transition-colors">
                    {runningId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-redline-500" /> : <s.icon className="h-3.5 w-3.5 text-redline-500" strokeWidth={1.75} />}
                  </span>
                  {s.label}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{s.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="text-sm font-semibold mb-1">Or pick who's texting</div>
          <p className="text-xs text-muted-foreground mb-3">Choose a verified {V.lc("person")} (recognized from the roster) or an unknown number, then type your own message below.</p>
          <div className="flex flex-wrap gap-2">
            {quickPhones.map((q, i) => {
              const selected = phone === q.phone;
              const isUnknown = q.kind === "unknown";
              return (
                <button key={`${q.phone}-${i}`} data-testid={`sim-phone-${q.phone}`} onClick={() => { setPhone(q.phone); reset(q.phone); }}
                  className={`rounded-full pl-3 pr-4 py-2 text-sm border transition-colors flex items-center gap-1.5 ${selected ? "bg-ink text-white border-ink" : isUnknown ? "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400" : "bg-sand-50 border-sand-200 hover:border-ink"}`}>
                  {isUnknown
                    ? <UserRound className={`h-3.5 w-3.5 shrink-0 ${selected ? "text-white" : "text-amber-600"}`} />
                    : <BadgeCheck className={`h-3.5 w-3.5 shrink-0 ${selected ? "text-emerald-300" : "text-emerald-600"}`} />}
                  {q.label}
                  {!isUnknown && q.verified && <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${selected ? "bg-emerald-400/30 text-emerald-100" : "bg-emerald-100 text-emerald-700"}`}>Verified</span>}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5">
            <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" /> Verified {V.lcs("person")} are recognized instantly by name & unit. <UserRound className="h-3.5 w-3.5 text-amber-600 ml-1" /> Unknown numbers get the sign-up / verify flow.
          </p>
        </div>
      </div>
    </div>
  );
}
